import { useState, useEffect } from 'react';

export type NetworkType = 'wifi' | '4g' | '3g' | '2g' | 'offline' | 'unknown';

interface NetworkStatus {
  isOnline: boolean;
  type: NetworkType;
  effectiveType?: string;
  displayLabel: 'Connecté' | 'Déconnecté';
}

/**
 * Hook pour détecter le statut réseau (WiFi, 4G, 3G, 2G, ou déconnecté)
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    type: navigator.onLine ? 'unknown' : 'offline',
    displayLabel: navigator.onLine ? 'Connecté' : 'Déconnecté',
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine;
      
      if (!isOnline) {
        setNetworkStatus({
          isOnline: false,
          type: 'offline',
          displayLabel: 'Déconnecté',
        });
        return;
      }

      // Essayer d'accéder à l'API Network Information
      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;

      if (connection) {
        const effectiveType = connection.effectiveType || connection.type;
        const type = connection.type;

        let networkType: NetworkType = 'unknown';

        // Déterminer le type de réseau
        if (type === 'wifi' || type === 'ethernet') {
          networkType = 'wifi';
        } else if (effectiveType === '4g' || (type === 'cellular' && connection.effectiveType === '4g')) {
          networkType = '4g';
        } else if (effectiveType === '3g' || (type === 'cellular' && connection.effectiveType === '3g')) {
          networkType = '3g';
        } else if (effectiveType === '2g' || (type === 'cellular' && connection.effectiveType === '2g')) {
          networkType = '2g';
        } else if (type === 'cellular') {
          // Si on a cellular mais pas d'effectiveType, on essaie de deviner
          const downlink = connection.downlink || 0;
          if (downlink >= 10) {
            networkType = '4g';
          } else if (downlink >= 1.5) {
            networkType = '3g';
          } else {
            networkType = '2g';
          }
        }

        setNetworkStatus({
          isOnline: true,
          type: networkType,
          effectiveType,
          displayLabel: 'Connecté',
        });
      } else {
        // API Network Information non disponible, on utilise juste online/offline
        setNetworkStatus({
          isOnline: true,
          type: 'unknown',
          displayLabel: 'Connecté',
        });
      }
    };

    // Mise à jour initiale
    updateNetworkStatus();

    // Écouter les changements d'état réseau
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Écouter les changements de connexion (si l'API est disponible)
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    if (connection) {
      connection.addEventListener('change', updateNetworkStatus);
    }

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  return networkStatus;
};

